/**
 * Cálculo puro de totales de surtido (PRE/POST cupón carrito).
 * Sin I/O Firebase — recibe datos del pedido ya cargados.
 */

export const DELIVERY_SERVICE_PLU = '9701'

export const DISCOUNT_STATUS = {
  NO_DISCOUNT: 'no_discount',
  APPLICABLE: 'applicable',
  UNSUPPORTED_DISCOUNT_TYPE: 'unsupported_discount_type',
  UNRESOLVED_TYPE: 'unresolved_type',
  REQUIRES_BUSINESS_DECISION: 'requires_business_decision',
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function roundMoney(value) {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 100
}

/**
 * @param {unknown} raw
 * @returns {'carrito'|'envio'|null}
 */
export function normalizeTipoDescuento(raw) {
  if (raw === undefined || raw === null || raw === '') return null
  const t = String(raw).trim().toLowerCase()
  if (t === 'carrito' || t === 'envio') return t
  return null
}

/**
 * Monto histórico del cupón en el pedido (no el maestro).
 * @param {object|null|undefined} pedido
 * @returns {number|null}
 */
export function parseDescuentoCupon(pedido) {
  if (!pedido) return null
  const raw = pedido['descuento cupon']
  if (raw === undefined || raw === null || raw === '') return null
  const amount = parseFloat(raw)
  return Number.isFinite(amount) ? amount : null
}

/**
 * @param {object|null|undefined} productosRecolectados
 * @returns {boolean}
 */
export function hasDeliveryServicePlu(productosRecolectados) {
  if (!productosRecolectados || typeof productosRecolectados !== 'object') return false
  return Object.keys(productosRecolectados).some(key => String(key).trim() === DELIVERY_SERVICE_PLU)
}

/**
 * @param {object|null|undefined} productosRecolectados
 * @returns {number}
 */
export function getDeliveryServiceImporte(productosRecolectados) {
  if (!hasDeliveryServicePlu(productosRecolectados)) return 0
  const node = productosRecolectados[DELIVERY_SERVICE_PLU] || productosRecolectados['9701']
  if (!node) {
    const key = Object.keys(productosRecolectados).find(k => String(k).trim() === DELIVERY_SERVICE_PLU)
    const found = key ? productosRecolectados[key] : null
    if (!found) return 0
    if (found.pickedTotalPrice === undefined || found.pickedTotalPrice === null) return 0
    return roundMoney(found.pickedTotalPrice)
  }
  if (node.pickedTotalPrice === undefined || node.pickedTotalPrice === null) return 0
  return roundMoney(node.pickedTotalPrice)
}

/**
 * @param {object|null|undefined} productosRecolectados
 * @returns {number}
 */
export function sumPickedTotalPrices(productosRecolectados) {
  if (!productosRecolectados || typeof productosRecolectados !== 'object') return 0
  let sum = 0
  Object.keys(productosRecolectados).forEach(codigo => {
    const row = productosRecolectados[codigo]
    if (!row) return
    if (row.pickedTotalPrice === undefined || row.pickedTotalPrice === null) return
    sum += parseFloat(row.pickedTotalPrice) || 0
  })
  return roundMoney(sum)
}

/**
 * @param {object|null|undefined} pedido
 * @param {{ tipoDescuento?: string|null }} [options] - tipo ya resuelto (snapshot o legacy)
 * @returns {object}
 */
export function computePedidoSurtidoTotals(pedido, options = {}) {
  const productosRecolectados = pedido?.ProductosRecolectados || {}
  const envio = roundMoney(pedido?.Envio ?? pedido?.envio ?? 0)

  const SubtotalSurtido = sumPickedTotalPrices(productosRecolectados)
  const has9701 = hasDeliveryServicePlu(productosRecolectados)
  const importe9701 = getDeliveryServiceImporte(productosRecolectados)

  const grossFulfilledTotal = has9701 ? SubtotalSurtido : roundMoney(SubtotalSurtido + envio)

  const eligibleFulfilledSubtotal = has9701
    ? roundMoney(SubtotalSurtido - importe9701)
    : SubtotalSurtido

  const tipoDescuento = normalizeTipoDescuento(
    options.tipoDescuento !== undefined ? options.tipoDescuento : pedido?.tipoDescuento
  )
  const rawDiscount = parseDescuentoCupon(pedido)
  const hasCuponCode = !!(pedido?.cupon && String(pedido.cupon).trim())

  let discountStatus = DISCOUNT_STATUS.NO_DISCOUNT
  let discountAmount = 0
  let totalsDeterminable = true
  let TotalSurtido = grossFulfilledTotal

  if (rawDiscount != null && rawDiscount > 0) {
    if (tipoDescuento === 'carrito') {
      if (rawDiscount >= eligibleFulfilledSubtotal) {
        discountStatus = DISCOUNT_STATUS.REQUIRES_BUSINESS_DECISION
        discountAmount = roundMoney(rawDiscount)
        totalsDeterminable = false
        TotalSurtido = null
      } else {
        discountStatus = DISCOUNT_STATUS.APPLICABLE
        discountAmount = roundMoney(rawDiscount)
        TotalSurtido = roundMoney(grossFulfilledTotal - discountAmount)
      }
    } else if (tipoDescuento === 'envio') {
      discountStatus = DISCOUNT_STATUS.UNSUPPORTED_DISCOUNT_TYPE
      discountAmount = 0
      TotalSurtido = grossFulfilledTotal
    } else if (hasCuponCode || rawDiscount > 0) {
      // Cupón/monto presente pero tipo no resoluble: no inventar "carrito"
      discountStatus = DISCOUNT_STATUS.UNRESOLVED_TYPE
      discountAmount = 0
      TotalSurtido = grossFulfilledTotal
    }
  }

  const metodoPago = String(pedido?.Pago || '').toLowerCase()
  const esPagoEnLinea =
    metodoPago === 'visa' || metodoPago === 'mastercard' || metodoPago === 'american_express'

  let totalPagadoOriginal = null
  let dineroDevolucion = null

  if (esPagoEnLinea && totalsDeterminable && TotalSurtido != null) {
    totalPagadoOriginal =
      pedido?.totalPagadoOriginal != null && pedido.totalPagadoOriginal !== ''
        ? roundMoney(pedido.totalPagadoOriginal)
        : roundMoney(pedido?.Total || 0)

    if (TotalSurtido < totalPagadoOriginal) {
      dineroDevolucion = roundMoney(totalPagadoOriginal - TotalSurtido)
    } else {
      dineroDevolucion = 0
    }
  }

  return {
    SubtotalSurtido,
    has9701,
    importe9701,
    Envio: envio,
    grossFulfilledTotal,
    eligibleFulfilledSubtotal,
    tipoDescuento,
    discountAmount,
    discountStatus,
    totalsDeterminable,
    TotalSurtido,
    esPagoEnLinea,
    totalPagadoOriginal,
    dineroDevolucion,
  }
}
